<template>
  <v-app>
    <!-- SCORECARD SUMMARY -->
    <v-row class="mb-4">
      <v-col cols="12">
        <h2 class="text-h6 font-weight-medium">
          🩺 Medizinische Langzeit-Scorecard (4 Wochen)
        </h2>
      </v-col>

      <v-col v-for="card in scoreCards" :key="card.key" cols="12" md="3">
        <v-card :color="card.color" dark rounded="lg" elevation="3">
          <v-card-text class="text-center">
            <div class="text-subtitle-2 mb-1">{{ card.title }}</div>
            <div class="text-h4 font-weight-bold">
              {{ card.score }}
            </div>
            <div class="text-caption">
              {{ card.statusText }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- MEDICAL DETAIL PANEL (8 categories + weight trend) + 12W sparklines -->
    <v-expansion-panels v-model="openPanels" class="mb-6">
      <v-expansion-panel>
        <v-expansion-panel-title>
          🧠 Medizinische Detailbewertung (4-Wochen-Mittel, Baseline 12W, Trend)
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <v-simple-table>
            <thead>
              <tr>
                <th style="width: 280px;">Kategorie</th>
                <th style="width: 150px;">4W Ø</th>
                <th style="width: 140px;">Ampel</th>
                <th style="width: 240px;">Trend vs 12W</th>
                <th style="width: 200px;">12W Sparkline</th>
                <th>Hinweis</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="m in medicalDetails" :key="m.key">
                <td>
                  <strong>{{ m.title }}</strong>
                </td>

                <td>
                  <span v-if="m.value != null">
                    {{ formatValue(m) }}
                  </span>
                  <span v-else>–</span>
                </td>

                <td>
                  <v-chip v-if="m.status" :color="chipColor(m.status)" dark small>
                    {{ chipText(m.status) }}
                  </v-chip>
                  <span v-else>–</span>
                </td>

                <td>
                  <span v-if="m.trend">
                    <span
                      :style="{
                        color: m.trend.color,
                        fontWeight: '700',
                        fontSize: '1.1em'
                      }"
                    >
                      {{ m.trend.arrow }}
                    </span>
                    <span class="text-caption grey--text">
                      {{ m.deltaText }}
                    </span>
                  </span>
                  <span v-else>–</span>
                </td>

            <td style="width: 220px;">
              <div class="spark-wrap">
                <v-sparkline
                  v-if="m.spark && m.spark.length >= 2"
                  :key="`${m.key}-${m.spark.length}-${sparkRenderNonce}`"
                  :model-value="m.spark"
                  :width="200"
                  :height="36"
                  :smooth="true"
                  :line-width="2"
                  :padding="4"
                  :auto-draw="true"
                  :color="sparklineColor(m)"
                />
                <span v-else class="text-caption text-medium-emphasis">–</span>
              </div>
            </td>

                <td class="text-caption grey--text">
                  {{ m.note }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="text-caption grey--text mt-3">
            Hinweis: Bewertung basiert auf 4-Wochen-Mittelwerten und einer 12-Wochen-Baseline (ohne die letzten 4 Wochen).
            Wearable-Daten dienen der Orientierung, nicht der Diagnose.
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card class="ma-4 pa-4" outlined>
      <v-card-text>
      <v-card-title class="d-flex align-center justify-space-between">
          <span>garmin_health</span>

          <v-btn
            color="primary"
            variant="elevated"
            size="small"
            @click="downloadHealthEntriesCsv"
            :disabled="!(healthEntries && healthEntries.length)"
          >
            CSV herunterladen
          </v-btn>
        </v-card-title>

        <v-data-table
          :headers="healthHeaders"
          :items="healthEntries ?? []"
          :items-per-page="25"
          dense
          class="elevation-1"
        >
          <!-- Header -->
          <template #headers>
            <tr>
              <th
                v-for="h in healthHeaders"
                :key="h.value"
                class="text-left"
                style="color:#ddd;font-weight:600;"
              >
                {{ h.text }}
              </th>
            </tr>
          </template>

          <!-- Datum -->
          <template #item.calendar_date="{ item }">
            {{ item.calendar_date }}
          </template>

          <!-- Gewicht -->
          <template #item.weight="{ item }">
            <span v-if="item.weight != null">
              {{ (item.weight / 1000).toFixed(1) }} kg
            </span>
            <span v-else>–</span>
          </template>

          <!-- BMI -->
          <template #item.bmi="{ item }">
            <span v-if="item.bmi != null">{{ Number(item.bmi).toFixed(1) }}</span>
            <span v-else>–</span>
          </template>

          <!-- Body Water -->
          <template #item.body_water="{ item }">
            <span v-if="item.body_water != null">
              {{ Number(item.body_water).toFixed(1) }} %
            </span>
            <span v-else>–</span>
          </template>

          <!-- Sleep -->
          <template #item.sleep_time_seconds="{ item }">
            <span v-if="item.sleep_time_seconds != null">
              {{ (item.sleep_time_seconds / 3600).toFixed(2) }} h
            </span>
            <span v-else>–</span>
          </template>

            <!-- HRV (letzte Nacht) -->
            <template #item.hrv_last_night_avg="{ item }">
              <v-chip
                v-if="item.hrv_last_night_avg !== null && item.hrv_last_night_avg !== undefined"
                :color="
                  item.hrv_last_night_avg < 32
                    ? 'red'
                    : item.hrv_last_night_avg <= 56
                    ? 'green'
                    : 'orange'
                "
                dark
                small
              >
                {{ item.hrv_last_night_avg }} ms
              </v-chip>
              <span v-else>–</span>
            </template>

          <template #item.deep_sleep_seconds="{ item }">
            <span v-if="item.deep_sleep_seconds != null">
              {{ (item.deep_sleep_seconds / 3600).toFixed(2) }} h
            </span>
            <span v-else>–</span>
          </template>

          <template #item.light_sleep_seconds="{ item }">
            <span v-if="item.light_sleep_seconds != null">
              {{ (item.light_sleep_seconds / 3600).toFixed(2) }} h
            </span>
            <span v-else>–</span>
          </template>

          <template #item.rem_sleep_seconds="{ item }">
            <span v-if="item.rem_sleep_seconds != null">
              {{ (item.rem_sleep_seconds / 3600).toFixed(2) }} h
            </span>
            <span v-else>–</span>
          </template>

          <template #item.awake_sleep_seconds="{ item }">
            <span v-if="item.awake_sleep_seconds != null">
              {{ (item.awake_sleep_seconds / 3600).toFixed(2) }} h
            </span>
            <span v-else>–</span>
          </template>

          <!-- Stress -->
          <template #item.average_stress_level="{ item }">
            <v-chip
              v-if="item.average_stress_level !== null"
              :color="
                item.average_stress_level < 25
                  ? 'green'
                  : item.average_stress_level < 35
                  ? 'orange'
                  : 'red'
              "
              dark
              small
            >
              {{ item.average_stress_level }}
            </v-chip>
            <span v-else>–</span>
          </template>

          <!-- Body Battery -->
          <template #item.body_battery_during_sleep="{ item }">
            <span v-if="item.body_battery_during_sleep != null">
              {{ item.body_battery_during_sleep }}
            </span>
            <span v-else>–</span>
          </template>

          <!-- Respiration -->
          <template #item.avg_waking_respiration_value="{ item }">
            <span v-if="item.avg_waking_respiration_value != null">
              {{ item.avg_waking_respiration_value }} /min
            </span>
            <span v-else>–</span>
          </template>

          <template #no-data>
            Keine Health-Daten in der DB vorhanden
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "HealthScorecardScreen",
    watch: {
      openPanels() {
        // beim Öffnen/Schließen neu rendern
        this.sparkRenderNonce++;
      },
    },

  computed: {
    ...mapGetters({
      healthEntries: "stateHealthEntries",
    }),

    // Averages over windows (4W + 12W baseline excluding last 4W)
    averages() {
      const avg = (arr) =>
        arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;

      const entries = (this.healthEntries ?? []).slice();

      const now = new Date();
      const cutoff4w = new Date(now);
      cutoff4w.setDate(cutoff4w.getDate() - 28);

      const cutoff12w = new Date(now);
      cutoff12w.setDate(cutoff12w.getDate() - 84);

      const in4w = entries.filter((e) => new Date(e.calendar_date) >= cutoff4w);
      const in12w = entries.filter((e) => new Date(e.calendar_date) >= cutoff12w);

      // baseline = 12W without last 4W
      const baseline12w = in12w.filter((e) => new Date(e.calendar_date) < cutoff4w);

      const toHours = (sec) => sec / 3600;
      const weightKg = (g) => g / 1000;

      const a4 = {
        restingHR: avg(in4w.map((e) => e.hr_resting).filter((v) => v != null)),
        avgSleepHR: avg(in4w.map((e) => e.avg_sleep_hr).filter((v) => v != null)),
        stress: avg(in4w.map((e) => e.average_stress_level).filter((v) => v != null)),
        bodyBattery: avg(in4w.map((e) => e.body_battery_during_sleep).filter((v) => v != null)),
        hrvLastNightAvg: avg(in4w.map((e) => e.hrv_last_night_avg).filter((v) => v != null)),
        sleepHours: avg(
          in4w
            .map((e) => e.sleep_time_seconds)
            .filter((v) => v != null)
            .map(toHours)
        ),
        deepSleepHours: avg(
          in4w
            .map((e) => e.deep_sleep_seconds)
            .filter((v) => v != null)
            .map(toHours)
        ),
        bodyFat: avg(in4w.map((e) => e.body_fat).filter((v) => v != null)),
        respiration: avg(in4w.map((e) => e.avg_waking_respiration_value).filter((v) => v != null)),
        weightKg: avg(
          in4w
            .map((e) => e.weight)
            .filter((v) => v != null)
            .map(weightKg)
        ),
      };

      const b12 = {
        restingHR: avg(baseline12w.map((e) => e.hr_resting).filter((v) => v != null)),
        avgSleepHR: avg(baseline12w.map((e) => e.avg_sleep_hr).filter((v) => v != null)),
        stress: avg(baseline12w.map((e) => e.average_stress_level).filter((v) => v != null)),
        hrvLastNightAvg: avg(baseline12w.map((e) => e.hrv_last_night_avg).filter((v) => v != null)),
        bodyBattery: avg(
          baseline12w.map((e) => e.body_battery_during_sleep).filter((v) => v != null)
        ),
        sleepHours: avg(
          baseline12w
            .map((e) => e.sleep_time_seconds)
            .filter((v) => v != null)
            .map(toHours)
        ),
        deepSleepHours: avg(
          baseline12w
            .map((e) => e.deep_sleep_seconds)
            .filter((v) => v != null)
            .map(toHours)
        ),
        bodyFat: avg(baseline12w.map((e) => e.body_fat).filter((v) => v != null)),
        respiration: avg(
          baseline12w.map((e) => e.avg_waking_respiration_value).filter((v) => v != null)
        ),
        weightKg: avg(
          baseline12w
            .map((e) => e.weight)
            .filter((v) => v != null)
            .map(weightKg)
        ),
      };

      const d = {
        restingHR:
          a4.restingHR != null && b12.restingHR != null ? a4.restingHR - b12.restingHR : null,
        hrvLastNightAvg:
          a4.hrvLastNightAvg != null && b12.hrvLastNightAvg != null
            ? a4.hrvLastNightAvg - b12.hrvLastNightAvg
            : null,
        avgSleepHR:
          a4.avgSleepHR != null && b12.avgSleepHR != null ? a4.avgSleepHR - b12.avgSleepHR : null,
        stress: a4.stress != null && b12.stress != null ? a4.stress - b12.stress : null,
        bodyBattery:
          a4.bodyBattery != null && b12.bodyBattery != null ? a4.bodyBattery - b12.bodyBattery : null,
        sleepHours:
          a4.sleepHours != null && b12.sleepHours != null ? a4.sleepHours - b12.sleepHours : null,
        deepSleepHours:
          a4.deepSleepHours != null && b12.deepSleepHours != null
            ? a4.deepSleepHours - b12.deepSleepHours
            : null,
        bodyFat: a4.bodyFat != null && b12.bodyFat != null ? a4.bodyFat - b12.bodyFat : null,
        respiration:
          a4.respiration != null && b12.respiration != null ? a4.respiration - b12.respiration : null,
        weightKg: a4.weightKg != null && b12.weightKg != null ? a4.weightKg - b12.weightKg : null,
      };

      return {
        ...a4,
        baseline12w: b12,
        deltaVs12w: d,
      };
    },

    // 12W series for sparklines (per metric)
    spark12w() {
      const entries = (this.healthEntries ?? []).slice();

      const now = new Date();
      const cutoff12w = new Date(now);
      cutoff12w.setDate(cutoff12w.getDate() - 84);

      const in12w = entries
        .filter((e) => new Date(e.calendar_date) >= cutoff12w)
        .sort((a, b) => new Date(a.calendar_date) - new Date(b.calendar_date));

      const toHours = (sec) => sec / 3600;
      const weightKg = (g) => g / 1000;

      const series = (mapper) => in12w.map(mapper).filter((v) => v != null && !Number.isNaN(v));

      return {
        restingHR: series((e) => e.hr_resting),
        hrvLastNightAvg: series((e) => e.hrv_last_night_avg),
        avgSleepHR: series((e) => e.avg_sleep_hr),
        stress: series((e) => e.average_stress_level),
        bodyBattery: series((e) => e.body_battery_during_sleep),
        sleepHours: series((e) => (e.sleep_time_seconds != null ? toHours(e.sleep_time_seconds) : null)),
        deepSleepHours: series((e) => (e.deep_sleep_seconds != null ? toHours(e.deep_sleep_seconds) : null)),
        bodyFat: series((e) => e.body_fat),
        respiration: series((e) => e.avg_waking_respiration_value),
        weightKg: series((e) => (e.weight != null ? weightKg(e.weight) : null)),
      };
    },

    // Summary cards (4 buckets) with trend arrows (colored shown in detail table; summary uses arrow only)
    scoreCards() {
      const heart = this.heartScore();
      const sleep = this.sleepScore();
      const stress = this.stressScore();
      const metab = this.metabolismScore();

      const d = this.averages.deltaVs12w || {};

      const mk = (key, title, score, statusText) => {
        const lvl = this.levelFromScore(score);
        return {
          key,
          title,
          score: Math.round(score),
          statusText,
          level: lvl,
          color: this.colorFromLevel(lvl),
        };
      };

      const hrTrend = this.trendInfo(d.restingHR, { higherIsBetter: false, deadband: 1.0 });
      const sleepTrend = this.trendInfo(d.sleepHours, { higherIsBetter: true, deadband: 0.2 });
      const stressTrend = this.trendInfo(d.stress, { higherIsBetter: false, deadband: 2.0 });
      const fatTrend = this.trendInfo(d.bodyFat, { higherIsBetter: false, deadband: 0.3 });

      return [
        mk(
          "heart",
          "❤️ Herz",
          heart,
          this.averages.restingHR != null
            ? `Ruhepuls Ø: ${this.averages.restingHR.toFixed(0)} bpm (${hrTrend.arrow} ${this.formatDelta(d.restingHR, 0)} vs 12W)`
            : "Ruhepuls Ø: –"
        ),
        mk(
          "sleep",
          "😴 Schlaf",
          sleep,
          this.averages.sleepHours != null
            ? `Schlaf Ø: ${this.averages.sleepHours.toFixed(1)} h (${sleepTrend.arrow} ${this.formatDelta(d.sleepHours, 1)} vs 12W)`
            : "Schlaf Ø: –"
        ),
        mk(
          "stress",
          "🧠 Stress",
          stress,
          this.averages.stress != null
            ? `Stress Ø: ${this.averages.stress.toFixed(0)} (${stressTrend.arrow} ${this.formatDelta(d.stress, 0)} vs 12W)`
            : "Stress Ø: –"
        ),
        mk(
          "metabolism",
          "⚖️ Stoffwechsel",
          metab,
          this.averages.bodyFat != null
            ? `Körperfett Ø: ${this.averages.bodyFat.toFixed(1)} % (${fatTrend.arrow} ${this.formatDelta(d.bodyFat, 1)} vs 12W)`
            : "Körperfett Ø: –"
        ),
      ];
    },

    // 8 medical categories + weight trend + 12W sparklines
    medicalDetails() {
      const a = this.averages;
      const b = this.averages.baseline12w || {};
      const d = this.averages.deltaVs12w || {};
      const s = this.spark12w || {};

      const deltaNight =
        a.avgSleepHR != null && a.restingHR != null ? a.avgSleepHR - a.restingHR : null;

      // For "Gewicht (Trend, 6 Monate)" we already use 12W delta here for baseline/trend arrows.
      // The "Ampel" still evaluates the absolute delta (as per your rule; you can switch to 6m delta if you prefer).
      const weightDelta12w = d.weightKg;

      return [
        {
          key: "resting_hr",
          title: "❤️ Ruhepuls",
          value: a.restingHR,
          unit: "bpm",
          status: this.evalRestingHR(a.restingHR),
          trend: this.trendInfo(d.restingHR, { higherIsBetter: false, deadband: 1.0 }),
          deltaText: `Δ ${this.formatDelta(d.restingHR, 0)} bpm vs 12W (${b.restingHR != null ? b.restingHR.toFixed(0) : "–"})`,
          spark: s.restingHR,
          note: "🟢 ≤55 | 🟡 56–60 | 🔴 >60 (über 2–3 Wochen)",
        },
        {
          key: "night_hr",
          title: "🌙 Nächtlicher Durchschnittspuls",
          value: deltaNight,
          unit: "Δ bpm",
          status: this.evalNightHR(a.avgSleepHR, a.restingHR),
          trend: this.trendInfo(d.avgSleepHR, { higherIsBetter: false, deadband: 1.0 }),
          deltaText: `Δ ${this.formatDelta(d.avgSleepHR, 0)} bpm (Avg Sleep HR) vs 12W (${b.avgSleepHR != null ? b.avgSleepHR.toFixed(0) : "–"})`,
          spark: s.avgSleepHR,
          note: "🟢 ≤ +3 | 🟡 +4–6 | 🔴 ≥ +7 oder steigend",
        },
        {
          key: "stress",
          title: "🧠 HRV (Stresslevel)",
          value: a.stress,
          unit: "",
          status: this.evalStress(a.stress),
          trend: this.trendInfo(d.stress, { higherIsBetter: false, deadband: 2.0 }),
          deltaText: `Δ ${this.formatDelta(d.stress, 0)} vs 12W (${b.stress != null ? b.stress.toFixed(0) : "–"})`,
          spark: s.stress,
          note: "🟢 <25 | 🟡 25–35 | 🔴 >35 (Trend über Wochen wichtig)",
        },
        {
          key: "hrv_last_night_avg",
          title: "💓 HRV (letzte Nacht)",
          value: a.hrvLastNightAvg,
          unit: "ms",
          status: this.evalHrv(a.hrvLastNightAvg),
          // HRV: höher ist tendenziell besser, aber deine grüne Zone ist 32–56.
          // Trend: wir werten "höher besser" (Arrow ↑ = besser), deadband 2ms
          trend: this.trendInfo(d.hrvLastNightAvg, { higherIsBetter: true, deadband: 2.0 }),
          deltaText: `Δ ${this.formatDelta(d.hrvLastNightAvg, 0)} ms vs 12W (${b.hrvLastNightAvg != null ? b.hrvLastNightAvg.toFixed(0) : "–"})`,
          spark: s.hrvLastNightAvg,
          note: "🟢 32–56 ms | 🔴 <32 ms | 🟡 >56 ms",
        },
        {
          key: "body_battery",
          title: "🔋 Regenerationsfähigkeit (Body Battery)",
          value: a.bodyBattery,
          unit: "",
          status: this.evalBodyBattery(a.bodyBattery),
          trend: this.trendInfo(d.bodyBattery, { higherIsBetter: true, deadband: 3.0 }),
          deltaText: `Δ ${this.formatDelta(d.bodyBattery, 0)} vs 12W (${b.bodyBattery != null ? b.bodyBattery.toFixed(0) : "–"})`,
          spark: s.bodyBattery,
          note: "🟢 >35 | 🟡 20–35 | 🔴 <20 (zeigt Erholung im Schlaf)",
        },
        {
          key: "sleep_total",
          title: "💤 Gesamtschlafdauer",
          value: a.sleepHours,
          unit: "h",
          status: this.evalSleepDuration(a.sleepHours),
          trend: this.trendInfo(d.sleepHours, { higherIsBetter: true, deadband: 0.2 }),
          deltaText: `Δ ${this.formatDelta(d.sleepHours, 1)} h vs 12W (${b.sleepHours != null ? b.sleepHours.toFixed(1) : "–"})`,
          spark: s.sleepHours,
          note: "🟢 7.0–8.5h | 🟡 6.0–7.0h | 🔴 <6.0h",
        },
        {
          key: "deep_sleep",
          title: "🌊 Tiefschlaf",
          value: a.deepSleepHours,
          unit: "h",
          status: this.evalDeepSleep(a.deepSleepHours),
          trend: this.trendInfo(d.deepSleepHours, { higherIsBetter: true, deadband: 0.15 }),
          deltaText: `Δ ${this.formatDelta(d.deepSleepHours, 2)} h vs 12W (${b.deepSleepHours != null ? b.deepSleepHours.toFixed(2) : "–"})`,
          spark: s.deepSleepHours,
          note: "🟢 ≥1.2h | 🟡 0.8–1.2h | 🔴 <0.8h",
        },
        {
          key: "body_fat",
          title: "⚖️ Körperfettanteil",
          value: a.bodyFat,
          unit: "%",
          status: this.evalBodyFat(a.bodyFat),
          trend: this.trendInfo(d.bodyFat, { higherIsBetter: false, deadband: 0.3 }),
          deltaText: `Δ ${this.formatDelta(d.bodyFat, 1)} % vs 12W (${b.bodyFat != null ? b.bodyFat.toFixed(1) : "–"})`,
          spark: s.bodyFat,
          note: "🟢 18–22% | 🟡 22–25% | 🔴 >25%",
        },
        {
          key: "weight_trend",
          title: "⚖️ Gewicht (Trend)",
          value: weightDelta12w,
          unit: "kg Δ",
          status: this.evalWeightTrend(weightDelta12w),
          trend: this.trendInfo(d.weightKg, { higherIsBetter: false, deadband: 0.3 }),
          deltaText: `Δ ${this.formatDelta(d.weightKg, 1)} kg vs 12W (${b.weightKg != null ? b.weightKg.toFixed(1) : "–"})`,
          spark: s.weightKg,
          note: "🟢 ±1 kg | 🟡 1–3 kg | 🔴 > 3 kg (Langzeittrend; ideal 6M)",
        },
        {
          key: "respiration",
          title: "🌬️ Atemfrequenz",
          value: a.respiration,
          unit: "/min",
          status: this.evalRespiration(a.respiration),
          trend: this.trendInfo(d.respiration, { higherIsBetter: false, deadband: 0.8 }),
          deltaText: `Δ ${this.formatDelta(d.respiration, 0)} /min vs 12W (${b.respiration != null ? b.respiration.toFixed(0) : "–"})`,
          spark: s.respiration,
          note: "🟢 10–14 | 🟡 15–17 | 🔴 >17 (Stress/Infekt/Apnoe möglich)",
        },
      ];
    },
  },

  data: () => ({
    healthHeaders: [
      { text: "Datum", value: "calendar_date" },
      { text: "Gewicht", value: "weight" },
      { text: "BMI", value: "bmi" },
      { text: "Body Fat", value: "body_fat" },
      { text: "Body Water", value: "body_water" },
      { text: "HR Resting", value: "hr_resting" },
      { text: "HR Min", value: "hr_min" },
      { text: "HR Max", value: "hr_max" },
      // ✅ NEU
      { text: "HRV (ms)", value: "hrv_last_night_avg" },

      { text: "Tiefschlaf (h)", value: "deep_sleep_seconds" },
      { text: "Stress", value: "average_stress_level" },
      { text: "Body Battery Schlaf", value: "body_battery_during_sleep" },
      { text: "Atemfrequenz", value: "avg_waking_respiration_value" },

      { text: "Sleep Score", value: "sleep_score" },
      { text: "Total Sleep", value: "sleep_time_seconds" },
      { text: "Deep", value: "deep_sleep_seconds" },
      { text: "Light", value: "light_sleep_seconds" },
      { text: "REM", value: "rem_sleep_seconds" },
      { text: "Awake", value: "awake_sleep_seconds" },
      { text: "Avg Sleep HR", value: "avg_sleep_hr" },
      { text: "Avg Sleep Stress", value: "avg_sleep_stress" },
    ],
    sparkRenderNonce: 0,
    openPanels: [0],
  }),

  mounted() {
    this.getHealthEntries();
  },

  methods: {
    ...mapActions(["getHealthEntries"]),

    sparklineColor(m) {
      if (!m || !m.trend || !m.trend.color || m.trend.color === "grey") return "#d0d0d0";
      return m.trend.color; // 'green'/'red'
    },

    downloadHealthEntriesCsv() {
      const items = this.healthEntries ?? [];
      if (!items.length) return;

      // Spaltenreihenfolge: nimm die gleiche Reihenfolge wie im Table-Header
      const columns = (this.healthHeaders ?? [])
        .map((h) => ({ key: h.value, label: h.text }))
        // falls du im Header doppelte keys hast (bei dir z.B. deep_sleep_seconds ist doppelt),
        // dann werden sie hier dedupliziert:
        .filter((col, idx, arr) => arr.findIndex((c) => c.key === col.key) === idx);

      const escapeCsv = (value) => {
        if (value === null || value === undefined) return "";
        const s = String(value);
        // CSV-Standard: wenn Komma/Quote/Newline enthalten -> in Quotes + Quotes verdoppeln
        if (/[",\n\r;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };

      const headerLine = columns.map((c) => escapeCsv(c.label)).join(";");
      const lines = items.map((row) =>
        columns.map((c) => escapeCsv(row?.[c.key])).join(";")
      );

      // UTF-8 BOM hilft Excel bei Umlauten
      const csv = "\uFEFF" + [headerLine, ...lines].join("\r\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const filename = `healthEntries_${new Date().toISOString().slice(0, 10)}.csv`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    },


    // Chip helpers
    chipColor(status) {
      return { good: "green", watch: "orange", alert: "red" }[status] || "grey";
    },
    chipText(status) {
      return { good: "🟢 gut", watch: "🟡 beobachten", alert: "🔴 abklären" }[status] || "–";
    },

    // Trend arrow + color (higherIsBetter decides improvement direction)
    trendInfo(delta, { higherIsBetter, deadband = 0.5 } = {}) {
      if (delta == null) return { arrow: "→", color: "grey", better: null };
      if (Math.abs(delta) < deadband) return { arrow: "→", color: "grey", better: null };

      const improvement = higherIsBetter ? delta > 0 : delta < 0;
      return { arrow: improvement ? "↑" : "↓", color: improvement ? "green" : "red", better: improvement };
    },
    evalHrv(ms) {
      if (ms == null) return null;
      if (ms < 32) return "alert";        // rot
      if (ms <= 56) return "good";        // grün
      return "watch";                      // gelb/orange
    },
    formatDelta(delta, digits = 1) {
      if (delta == null) return "–";
      const sign = delta > 0 ? "+" : "";
      return `${sign}${Number(delta).toFixed(digits)}`;
    },

    formatValue(m) {
      const v = Number(m.value);
      if (m.value == null) return "–";
      if (m.unit === "ms") return `${v.toFixed(0)} ms`;
      if (Number.isNaN(v)) return "–";

      if (m.unit === "h") return `${v.toFixed(2)} h`;
      if (m.unit === "%") return `${v.toFixed(1)} %`;
      if (m.unit === "bpm") return `${v.toFixed(0)} bpm`;
      if (m.unit === "Δ bpm") return `${v.toFixed(0)} Δ bpm`;
      if (m.unit === "kg Δ") {
        const sign = v > 0 ? "+" : "";
        return `${sign}${v.toFixed(1)} kg`;
      }
      if (m.unit === "/min") return `${v.toFixed(0)} /min`;
      return `${v.toFixed(1)} ${m.unit}`.trim();
    },

    // Ampel levels based on score (0..100)
    levelFromScore(score) {
      if (score >= 80) return "good";
      if (score >= 60) return "watch";
      return "alert";
    },
    colorFromLevel(level) {
      return { good: "green darken-2", watch: "orange darken-2", alert: "red darken-2" }[level];
    },

    // ---- 4 bucket scores (simple heuristic) ----
    heartScore() {
      const hr = this.averages.restingHR;
      if (hr == null) return 0;
      const penalty = Math.max(0, hr - 55) * 6;
      return Math.max(0, Math.min(100, 100 - penalty));
    },
    sleepScore() {
      const sleep = this.averages.sleepHours;
      const deep = this.averages.deepSleepHours;
      if (sleep == null || deep == null) return 0;

      let score = 100;
      if (sleep < 7) score -= (7 - sleep) * 20;
      if (sleep > 8.5) score -= (sleep - 8.5) * 10;

      if (deep < 1.2) score -= (1.2 - deep) * 40;
      return Math.max(0, Math.min(100, score));
    },
    stressScore() {
      const s = this.averages.stress;
      if (s == null) return 0;
      if (s < 25) return 90;
      if (s <= 35) return 70;
      return 45;
    },
    metabolismScore() {
      const bf = this.averages.bodyFat;
      if (bf == null) return 75;
      if (bf <= 22) return 90;
      if (bf <= 25) return 70;
      return 45;
    },

    // ---- 8 category evaluation (exact thresholds) ----
    evalRestingHR(hr) {
      if (hr == null) return null;
      if (hr <= 55) return "good";
      if (hr <= 60) return "watch";
      return "alert";
    },
    evalNightHR(avgSleepHR, restingHR) {
      if (avgSleepHR == null || restingHR == null) return null;
      const delta = avgSleepHR - restingHR;
      if (delta <= 3) return "good";
      if (delta <= 6) return "watch";
      return "alert";
    },
    evalStress(stress) {
      if (stress == null) return null;
      if (stress < 25) return "good";
      if (stress <= 35) return "watch";
      return "alert";
    },
    evalBodyBattery(bb) {
      if (bb == null) return null;
      if (bb > 35) return "good";
      if (bb >= 20) return "watch";
      return "alert";
    },
    evalSleepDuration(hours) {
      if (hours == null) return null;
      if (hours >= 7.0) return "good";
      if (hours >= 6.0) return "watch";
      return "alert";
    },
    evalDeepSleep(hours) {
      if (hours == null) return null;
      if (hours >= 1.2) return "good";
      if (hours >= 0.8) return "watch";
      return "alert";
    },
    evalBodyFat(fat) {
      if (fat == null) return null;
      if (fat <= 22) return "good";
      if (fat <= 25) return "watch";
      return "alert";
    },
    // weight trend evaluation (uses absolute delta; ideal is 6M, but we apply on 12W delta here)
    evalWeightTrend(deltaKg) {
      if (deltaKg == null) return null;
      const d = Math.abs(deltaKg);
      if (d <= 1) return "good";
      if (d <= 3) return "watch";
      return "alert";
    },
    evalRespiration(rate) {
      if (rate == null) return null;
      if (rate <= 14) return "good";
      if (rate <= 17) return "watch";
      return "alert";
    },
  },
};
</script>

<style scoped>
.spark-wrap {
  width: 200px;
  min-width: 200px;
  height: 36px;
  display: block;
  overflow: visible;
}

/* Vuetify 3: Sparkline rendert ein SVG – das muss explizit Größe bekommen */
.spark-wrap :deep(svg) {
  width: 200px !important;
  height: 36px !important;
  display: block !important;
  overflow: visible !important;
}
</style>
